import { useCallback, useMemo, useState, type FormEvent } from "react";
import { Clock3, Fingerprint, LifeBuoy, Plus, ShieldCheck } from "lucide-react";
import type { SessionView } from "@control-plane/contracts";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";
import { PageHeader } from "../components/PageHeader";
import { Dialog } from "../components/Overlay";
import { V3Select, type SelectOption } from "../components/V3Select";
import { StatusBadge } from "../components/StatusBadge";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { RemotePageState } from "../components/RemotePageState";
import { AiExplainError, AiRiskCheck } from "../components/AssistantInlineHelp";
import { supportCapabilities } from "../lib/pageCapabilities";

type SupportGrantRecord = Record<string, unknown>;
type SupportChoice = { label: string; selectionToken: string; summary: string; disabled?: boolean };
type AccessProfile = { key: string; label: string; minDurationMinutes: number; maxDurationMinutes: number };
type SupportTargets = { tenants: SupportChoice[]; operators: SupportChoice[]; accessProfiles: AccessProfile[]; error?: string };
type SupportGrantView = {
  key: string;
  tenantLabel: string;
  operatorLabel: string;
  requesterLabel: string;
  approverLabel: string;
  reason: string;
  status: string;
  statusLabel: string;
  expiresAtText: string;
  remainingText: string;
  permissionSummary: string;
  continuousIdentity: string;
  canApprove: boolean;
  canActivate: boolean;
  canEnd: boolean;
};
type SupportConsole = { grants: SupportGrantView[]; targets: SupportTargets };
type SupportSessionView = { tenantLabel: string; operatorLabel: string; permissionSummary: string; remainingText: string; continuousIdentity: string; ticketNotice: string };

const fallbackAccessProfiles: AccessProfile[] = [
  { key: "diagnostics_read_only", label: "只读诊断", minDurationMinutes: 15, maxDurationMinutes: 60 },
];

function createRequestKey(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function recordOf(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function arrayOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringOf(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function safeText(value: unknown, fallback: string) {
  const text = stringOf(value);
  if (!text) return fallback;
  return text.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, "所选对象").slice(0, 220);
}

function formatTime(value: unknown, fallback = "尚无时间") {
  const text = stringOf(value);
  if (!text) return fallback;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "时间待确认";
  return date.toLocaleString("zh-CN");
}

function remainingText(value: unknown, expiresAt: unknown) {
  const seconds = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(seconds) && seconds > 0) {
    const minutes = Math.ceil(seconds / 60);
    return `剩余时间 ${minutes} 分钟`;
  }
  const text = formatTime(expiresAt, "");
  return text ? `剩余时间至 ${text}` : "剩余时间待平台确认";
}

function normalizeChoice(value: unknown, fallback: string): SupportChoice | null {
  const record = recordOf(value);
  const token = stringOf(record.selectionToken);
  const label = stringOf(record.label ?? record.name ?? record.displayName);
  if (!token || !label) return null;
  return {
    selectionToken: token,
    label,
    summary: safeText(record.summary ?? record.statusLabel ?? record.capabilityLabel, fallback),
    disabled: Boolean(record.disabled),
  };
}

function normalizeTargets(value: unknown): SupportTargets {
  const record = recordOf(value);
  return {
    tenants: arrayOf(record.tenants).map((item) => normalizeChoice(item, "可代管企业")).filter((item): item is SupportChoice => Boolean(item)),
    operators: arrayOf(record.operators).map((item) => normalizeChoice(item, "可执行代管的操作员")).filter((item): item is SupportChoice => Boolean(item)),
    accessProfiles: arrayOf(record.accessProfiles).map((item) => {
      const profile = recordOf(item);
      const key = stringOf(profile.key);
      const label = stringOf(profile.label);
      if (!key || !label) return null;
      return {
        key,
        label,
        minDurationMinutes: Number(profile.minDurationMinutes) || 15,
        maxDurationMinutes: Number(profile.maxDurationMinutes) || 60,
      };
    }).filter((item): item is AccessProfile => Boolean(item)).concat(arrayOf(record.accessProfiles).length ? [] : fallbackAccessProfiles),
  };
}

function normalizeGrant(value: SupportGrantRecord): SupportGrantView {
  const expiresAt = value.expiresAt;
  const status = stringOf(value.status) ?? "pending";
  return {
    key: safeText(value.handle ?? value.id, "support-handle"),
    tenantLabel: safeText(value.tenantLabel ?? value.tenantName, "企业待确认"),
    operatorLabel: safeText(value.operatorLabel ?? value.operatorName, "操作员待确认"),
    requesterLabel: safeText(value.requesterLabel ?? value.requestedByName, "申请人待确认"),
    approverLabel: safeText(value.approverLabel ?? value.approvedByName, "待双人审批"),
    reason: safeText(value.reason, "代管原因待补充"),
    status,
    statusLabel: safeText(value.statusLabel, status === "active" ? "进行中" : "待双人审批"),
    expiresAtText: formatTime(expiresAt),
    remainingText: remainingText(value.remainingSeconds ?? value.remainingMinutes, expiresAt),
    permissionSummary: safeText(value.permissionSummary ?? value.accessProfileLabel, "只读诊断"),
    continuousIdentity: safeText(value.continuousIdentity, "持续身份待平台确认"),
    canApprove: Boolean(value.canApprove) || status === "pending",
    canActivate: Boolean(value.canActivate) || status === "approved",
    canEnd: Boolean(value.canEnd) || status === "active",
  };
}

function normalizeSession(value: unknown): SupportSessionView {
  const record = recordOf(value);
  const session = recordOf(record.session);
  return {
    tenantLabel: safeText(session.tenantLabel, "对应企业"),
    operatorLabel: safeText(session.operatorLabel, "当前操作员"),
    permissionSummary: safeText(session.permissionSummary, "只读诊断"),
    remainingText: remainingText(session.remainingSeconds, null),
    continuousIdentity: safeText(session.continuousIdentity, "持续身份由当前浏览器保持"),
    ticketNotice: record.oneTimeTicket ? "短时交换票据已签发，页面不会展示票据内容。" : "代管会话已建立。",
  };
}

function choiceOptions(items: SupportChoice[], emptyLabel: string): SelectOption[] {
  return items.length
    ? items.map((item) => ({ value: item.selectionToken, label: item.label, hint: item.summary, ...(item.disabled ? { disabled: true } : {}) }))
    : [{ value: "", label: emptyLabel, hint: "等待平台返回可选对象", disabled: true }];
}

function durationOptions(profile: AccessProfile): SelectOption[] {
  const candidates = [profile.minDurationMinutes, 15, 30, 60, profile.maxDurationMinutes]
    .filter((value) => value >= profile.minDurationMinutes && value <= profile.maxDurationMinutes);
  return Array.from(new Set(candidates)).sort((a, b) => a - b).map((value) => ({ value: String(value), label: `${value} 分钟` }));
}

export function SupportPage({ session }: { session: SessionView }) {
  const caps = supportCapabilities(session.actions);
  const [showCreate, setShowCreate] = useState(false);
  const [activeSession, setActiveSession] = useState<SupportSessionView | null>(null);
  const [feedback, setFeedback] = useState("");
  const [actionKeys] = useState(() => new Map<string, string>());
  const loader = useCallback(async (): Promise<SupportConsole> => {
    const [grantData, targetsResult] = await Promise.all([
      api<{ items: SupportGrantRecord[] }>("/api/support-grants"),
      caps.showCreateGrant
        ? api<unknown>("/api/support-grant-targets").then((value) => ({ value })).catch((error) => ({ error: error instanceof Error ? error.message : "代管目标读取失败" }))
        : Promise.resolve({ value: {} as unknown }),
    ]);
    const targets = "error" in targetsResult
      ? { ...normalizeTargets({}), error: targetsResult.error }
      : normalizeTargets(targetsResult.value);
    return { grants: grantData.items.map(normalizeGrant), targets };
  }, [caps.showCreateGrant]);
  const remote = useRemote(loader, (value) => value.grants.length === 0);

  const columns = useMemo<Array<DataTableColumn<SupportGrantView>>>(() => [
    {
      key: "target",
      header: "企业 / 操作员",
      width: "24%",
      render: (item) => <span className="security-person-cell"><strong>{item.tenantLabel}</strong><small>操作员 {item.operatorLabel}</small><small>{item.continuousIdentity}</small></span>,
    },
    { key: "reason", header: "原因", width: "22%", render: (item) => item.reason },
    { key: "status", header: "状态", width: "16%", render: (item) => <span className="security-person-cell"><StatusBadge value={item.status} /><small>{item.statusLabel}</small></span> },
    { key: "remaining", header: "剩余时间", width: "18%", render: (item) => <span className="security-person-cell"><strong className="app-number">{item.remainingText}</strong><small>到期后自动失效</small></span> },
    { key: "approval", header: "审批", width: "20%", render: (item) => <span className="security-person-cell"><small>申请：{item.requesterLabel}</small><small>审批：{item.approverLabel}</small><small>{item.permissionSummary}</small></span> },
  ], []);

  function actionRequestKey(item: SupportGrantView, action: "activate" | "revoke") {
    const key = `${item.key}:${action}`;
    const existing = actionKeys.get(key);
    if (existing) return existing;
    const next = createRequestKey(`support-${action}`);
    actionKeys.set(key, next);
    return next;
  }

  async function grantAction(item: SupportGrantView, action: "activate" | "revoke") {
    setFeedback("");
    try {
      const result = await api<unknown>(`/api/support-grants/${encodeURIComponent(item.key)}/${action}`, {
        method: "POST",
        body: JSON.stringify({ reason: `${item.tenantLabel}代管${action === "activate" ? "激活" : "结束"}。`, idempotencyKey: actionRequestKey(item, action) }),
      });
      if (action === "activate") setActiveSession(normalizeSession(result));
      setFeedback(action === "activate" ? "代管已激活，持续身份与剩余时间已展示。" : "代管已结束，后续访问会被阻断。");
      await remote.reload();
      window.dispatchEvent(new Event("support-identity-refresh"));
    } catch (failure) {
      setFeedback(failure instanceof Error ? failure.message : "代管操作失败，请重试。");
    }
  }

  return <div className="page">
    <PageHeader
      icon={Fingerprint}
      title="受控代管"
      meta={<span>待双人审批、持续身份、到期后自动失效</span>}
      actions={caps.showCreateGrant ? <button className="button primary" onClick={() => setShowCreate(true)}><Plus size={17} />申请代管</button> : null}
    />
    <div className="security-banner"><Fingerprint /><div><strong>持续身份</strong><span>代管只在批准、激活和有效期内可用；到期后自动失效，结束代管会立即停止访问。</span></div></div>
    {activeSession && <div className="success-message security-feedback"><ShieldCheck size={16} aria-hidden />{activeSession.operatorLabel} 正在代管 {activeSession.tenantLabel}，{activeSession.permissionSummary}，{activeSession.remainingText}。{activeSession.ticketNotice}</div>}
    {feedback && <div className="security-risk-note" role="status">{feedback}</div>}
    <SectionCard
      icon={LifeBuoy}
      title="代管申请"
      variant="ledger"
      meta={remote.state.status === "ready" || remote.state.status === "empty" ? `${remote.state.data.grants.length} 条` : "正在读取"}
    >
      <RemotePageState state={remote.state} onRetry={remote.reload} emptyHint="当前没有代管申请。">
        {(data) => <DataTable
          ariaLabel="受控代管申请"
          rows={data.grants}
          columns={columns}
          rowKey={(item) => item.key}
          rowActions={(item) => <div className="inline-actions">
            {caps.showApprove && item.canApprove && <span className="ops-action-note">请在审批中心批准</span>}
            {caps.showActivate && item.canActivate && <button type="button" className="button primary" onClick={() => void grantAction(item, "activate")}>激活</button>}
            {caps.showRevoke && item.canEnd && <button type="button" className="button ghost danger" onClick={() => void grantAction(item, "revoke")}>结束代管</button>}
            <AiRiskCheck action="受控代管" detail={`${item.tenantLabel} · ${item.operatorLabel} · ${item.permissionSummary}，${item.remainingText}。`} />
          </div>}
          empty="当前没有代管申请。"
        />}
      </RemotePageState>
    </SectionCard>
    {remote.state.status === "ready" && showCreate && <CreateGrantDialog targets={remote.state.data.targets} onClose={() => setShowCreate(false)} onDone={async () => { setShowCreate(false); await remote.reload(); }} />}
  </div>;
}

function CreateGrantDialog({ targets, onClose, onDone }: { targets: SupportTargets; onClose: () => void; onDone: () => Promise<void> }) {
  const firstTenant = targets.tenants.find((item) => !item.disabled)?.selectionToken ?? "";
  const firstOperator = targets.operators.find((item) => !item.disabled)?.selectionToken ?? "";
  const firstProfile = targets.accessProfiles[0] ?? fallbackAccessProfiles[0]!;
  const [form, setForm] = useState({ tenantSelectionToken: firstTenant, operatorSelectionToken: firstOperator, accessProfile: firstProfile.key, durationMinutes: firstProfile.minDurationMinutes, reason: "" });
  const [requestKey] = useState(() => createRequestKey("support-grant"));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const selectedProfile = targets.accessProfiles.find((item) => item.key === form.accessProfile) ?? firstProfile;
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!form.tenantSelectionToken || !form.operatorSelectionToken) { setError("平台尚未返回可选企业或操作员，暂不能提交。"); return; }
    if (form.reason.trim().length < 12) { setError("请填写客户授权、排查事项和结束条件，平台不会代替你生成申请理由。"); return; }
    const reason = form.reason.trim();
    setBusy(true);
    setError("");
    try {
      await api("/api/support-grants", {
        method: "POST",
        body: JSON.stringify({
          tenantSelectionToken: form.tenantSelectionToken,
          operatorSelectionToken: form.operatorSelectionToken,
          accessProfile: form.accessProfile,
          reason,
          durationMinutes: form.durationMinutes,
          idempotencyKey: requestKey,
        }),
      });
      await onDone();
      window.dispatchEvent(new Event("support-identity-refresh"));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "申请提交失败，表单内容已保留。");
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title="申请代管" description="从平台返回的业务对象中选择；最短权限、最短时间，审批通过后才可激活。" onClose={onClose}>
    <form className="form-grid" onSubmit={submit}>
      {targets.error && <div className="form-error full">代管对象读取失败：{targets.error}</div>}
      <label className="full">企业<V3Select value={form.tenantSelectionToken} onChange={(value) => setForm({ ...form, tenantSelectionToken: value })} options={choiceOptions(targets.tenants, "暂无可选企业")} ariaLabel="企业" /></label>
      <label className="full">操作员<V3Select value={form.operatorSelectionToken} onChange={(value) => setForm({ ...form, operatorSelectionToken: value })} options={choiceOptions(targets.operators, "暂无可选操作员")} ariaLabel="操作员" /></label>
      <label>最短权限<V3Select value={form.accessProfile} onChange={(value) => {
        const nextProfile = targets.accessProfiles.find((profile) => profile.key === value) ?? firstProfile;
        setForm({ ...form, accessProfile: value, durationMinutes: nextProfile.minDurationMinutes });
      }} options={targets.accessProfiles.map((profile) => ({ value: profile.key, label: profile.label, hint: `${profile.minDurationMinutes}-${profile.maxDurationMinutes} 分钟` }))} ariaLabel="最短权限" /></label>
      <label>有效时长<V3Select value={String(form.durationMinutes)} onChange={(value) => setForm({ ...form, durationMinutes: Number(value) })} options={durationOptions(selectedProfile)} ariaLabel="有效时长" /></label>
      <label className="full">代管原因<textarea name="reason" aria-label="原因" rows={3} value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} placeholder="请说明客户授权、排查事项和结束条件" /></label>
      <div className="security-risk-note full" role="note"><Clock3 size={16} aria-hidden />只读诊断优先；到期后自动失效，激活后页面会持续展示身份与剩余时间。</div>
      {error && <div className="form-error full inline-feedback"><span>{error}</span><AiExplainError message={error} context="申请代管" /></div>}
      <div className="form-actions full">
        <button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button>
        <button className="button primary" disabled={busy}>{busy ? "正在提交…" : "提交申请"}</button>
      </div>
    </form>
  </Dialog>;
}
